import React, { useRef, useState, useEffect } from "react";
import Sticky from "react-sticky-el";
import { arrayOf, string, bool } from "prop-types";
import styled, { useTheme } from "styled-components";
import { useMedia } from "react-use";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";

import ContentWrapper from "@components/shared/ContentWrapper";
import DesktopContainer from "@components/shared/DesktopContainer";
import MobileContainer from "@components/shared/MobileContainer";
import TitleSection from "./TitleSection";
import MapSection from "./MapSection";
import TestDrivePointsSection from "./TestDrivePointsSection";

import { desktop } from "@components/shared/utils";
import { getDistanceFromAddress } from "@lib/google-maps/getDistanceFromAddress";
import { calculateDistanceInMetersSync } from "@lib/google-maps/calculateDistance";
import { useMap } from "@shared/v2/widgets/useMap";
import { useGetTestDrivePointImage } from "@hooks/useGetTestDrivePointImage";
import { usePrimaryColor } from "@hooks/usePrimaryColor";
import { TEST_DRIVE_POINT_STORE_TYPE } from "@shared/constants";
import { getStaticAssetURL } from "@lib/getStaticAssetURL";
import { useScrollToView } from "@hooks/useScrollToView";
import { getIsProductionMode } from "@lib/getIsProductionMode";

import api from "@api";
import gtm from "@gtm/core";
import { getHomeTestDriveInfo } from "@lib/test-drive/getHomeTestDriveInfo";

import { TEST_DRIVE_TYPE_ID } from "@shared/constants";

import {
  testDriveLocationType,
  testDriveType,
  testDriveVehicleType,
  homeTestDriveLocationType,
} from "@types";

import {
  testDriveSearchResultsUpdateUrlEvent,
  testDriveMapUpdateUrlEvent,
} from "@gtm/events/testdrive";

import {
  useGoogleMapsWidgetSettingsSelector,
  useIsParkopolyEnabledSelector,
} from "@redux/reducers/appConfig";

import {
  useTestDriveAddressSelector,
  useTestDrivePointSelector,
} from "@redux/reducers/testDrive";

import {
  setTestDriveAddressAction,
  setTestDrivePointAction,
  setTestDriveSlotAction,
} from "@redux/actions/testDriveActions";

const MainContent = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  & > section {
    min-height: 0px !important;
  }
  align-items: stretch;

  ${desktop`
    flex-direction: row;
  `}
`;

const MapWrapper = styled.div`
  width: 100%;

  ${desktop`
    width: 50%;
  `}
`;

const Wrapper = styled(ContentWrapper)`
  padding: 0;

  ${desktop`
    min-height: calc(100vh - 19rem);
    padding: 0 2rem;
  `}
`;

const DesktopTextContentWrapper = styled.div`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  width: 50%;
`;

const sortByActivePointId = (deliveryPoints, activePointId) => {
  return [...deliveryPoints].sort(({ _id }) =>
    _id === activePointId ? -1 : 0
  );
};

const TestDrive = ({
  affiliation,
  locations,
  types,
  vehicles,
  homeTestDriveLocations,
  preDefinedTestDriveType,
  isSSRRendered,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const primaryColor = usePrimaryColor();
  const { defaultCenter, defaultZoom } = useGoogleMapsWidgetSettingsSelector();
  const scrollToView = useScrollToView();
  const isProduction = getIsProductionMode();

  const isParkopolyEnabled = useIsParkopolyEnabledSelector();

  const getTestDrivePointImage = useGetTestDrivePointImage();

  const typesContainerRef = useRef(null);
  const mapContainerRefMobile = useRef(null);
  const mapContainerRefDesktop = useRef(null);
  const [typeFilter, setTypeFilter] = useState(null);

  const [isPointSortingEnabled, setIsPointSortingEnabled] = useState(false);

  const [isHomeTestDriveAvailable, setIsHomeTestDriveAvailable] = useState(
    false
  );
  const address = useTestDriveAddressSelector();
  const selectedTestDrivePoint = useTestDrivePointSelector();
  const dispatch = useDispatch();
  const [filteredTestDrivePoints, setFilteredTestDrivePoints] = useState([]);
  const [isTypeExplanationOpen, setIsTypeExplanationOpen] = useState(true);
  const testDriveTypeWrapperRef = useRef(null);
  const isDesktopMedia = useMedia(`(min-width: ${theme.breakPoints.desktop})`);

  const [homeTestDrivePoint, setHomeTestDrivePoint] = useState(false);

  const map = useMap({
    containerRef: isDesktopMedia
      ? mapContainerRefDesktop
      : mapContainerRefMobile,

    options: {
      zoom: defaultZoom,
      center: defaultCenter,
      options: {
        disableDefaultUI: true,
      },
    },
  });

  const handleMarkerClick = id => {
    const targetTestDrivePoint = filteredTestDrivePoints.find(
      ({ _id: pointId }) => pointId === id
    );

    dispatch(setTestDrivePointAction(targetTestDrivePoint));
    dispatch(setTestDriveSlotAction(null));

    scrollToView(testDriveTypeWrapperRef);

    map?.instance.panTo({
      lat: Number(targetTestDrivePoint.geoLatitude),
      lng: Number(targetTestDrivePoint.geoLongitude),
    });

    const TARGET_ZOOM = 10;
    const currentZoom = map?.instance.getZoom();

    if (currentZoom < TARGET_ZOOM) {
      map?.smoothZoom(TARGET_ZOOM);
    }
  };

  const includeHomeTestDrivePoint = locations =>
    locations.some(point => point.id === homeTestDrivePoint.id)
      ? locations
      : locations.concat(homeTestDrivePoint);

  const excludeHomeTestDrivePoint = locations =>
    locations.filter(point => point.id !== homeTestDrivePoint?.id);

  const getMergedTestDrivePoints = async (locations, vehicles) => {
    const locationsWithDistance = await Promise.all(
      locations.map(async location => {
        const { geoLatitude, geoLongitude } = location;

        const distanceInMeters = await getDistanceFromAddress(
          geoLatitude,
          geoLongitude,
          address,
          selectedTestDrivePoint
        );

        return {
          ...location,
          distanceInMeters,
        };
      })
    );

    return vehicles.reduce(async (accPromise, vehicle) => {
      const acc = await accPromise;
      const distanceInMeters = await getDistanceFromAddress(
        vehicle.geoLatitude,
        vehicle.geoLongitude,
        address,
        selectedTestDrivePoint
      );

      return [
        ...acc,
        {
          ...vehicle,
          type: TEST_DRIVE_TYPE_ID.F2M,
          distanceInMeters,
        },
      ];
    }, locationsWithDistance);
  };

  const checkIsHomeTestDriveAvailable = async (address, locations) => {
    const {
      isHomeTestDriveAvailable,
      homeTestDrivePoint,
    } = await getHomeTestDriveInfo(
      address,
      locations,
      theme.colors.citroenBlue
    );

    setHomeTestDrivePoint(homeTestDrivePoint);
    setIsHomeTestDriveAvailable(isHomeTestDriveAvailable);
  };

  const handleSetAddress = async address => {
    if (isParkopolyEnabled) {
      try {
        await checkIsHomeTestDriveAvailable(address, homeTestDriveLocations);
      } catch (error) {
        // TODO Log error to Sentry ???
        if (!isProduction) {
          console.error(
            `❌ Couldn't fetch home test drive availability`,
            error
          );
        }
      }
    }

    setIsPointSortingEnabled(true);
    dispatch(setTestDriveAddressAction(address));
    return api.updateSession({ testDrive: { address } });
  };

  const handleResetAddress = () => {
    setIsPointSortingEnabled(true);
    setIsTypeExplanationOpen(true);

    dispatch(setTestDriveAddressAction(null));

    map?.instance.setCenter(defaultCenter);
    map?.smoothZoom(6);
  };

  const filteredTestDrivePointsLength = filteredTestDrivePoints.length;

  useEffect(() => {
    if (!homeTestDriveLocations.length || !isParkopolyEnabled) {
      return;
    }

    checkIsHomeTestDriveAvailable(address, homeTestDriveLocations);
  }, [homeTestDriveLocations]);

  useEffect(() => {
    // This handles the case when user selects a point and then presses the back button in the browser
    // We need this page to always have point sorted enabled when it is opened
    setIsPointSortingEnabled(true);

    // This handles the case when user comes from a link with predefined test drive type
    if (
      preDefinedTestDriveType &&
      Object.values(TEST_DRIVE_TYPE_ID).find(
        typeId => typeId === preDefinedTestDriveType
      ) &&
      types.find(type => type.id === preDefinedTestDriveType)
    ) {
      setTypeFilter(preDefinedTestDriveType);
    }
  }, []);

  useEffect(() => {
    // pointSortingEnabled is disabled when we click the select point button and are transitioning
    // to the next page in the journey (this prevents visually unpleasant resorting during the transition animation)
    if (!isPointSortingEnabled && filteredTestDrivePointsLength) {
      // Do not change the list sorting

      return;
    }

    if (!!address || !!typeFilter || !!selectedTestDrivePoint) {
      setIsTypeExplanationOpen(false);
    }

    getMergedTestDrivePoints(locations, vehicles).then(
      mergedTestDrivePoints => {
        mergedTestDrivePoints = isHomeTestDriveAvailable
          ? includeHomeTestDrivePoint(mergedTestDrivePoints)
          : excludeHomeTestDrivePoint(mergedTestDrivePoints);

        const sortedTestDrivePoints = isPointSortingEnabled
          ? [...mergedTestDrivePoints].sort(
              (a, b) => a.distanceInMeters - b.distanceInMeters
            )
          : mergedTestDrivePoints;

        const filteredPoints =
          typeFilter === null
            ? sortedTestDrivePoints
            : sortedTestDrivePoints.filter(({ type }) => type === typeFilter);

        let sortedPoints;
        if (isPointSortingEnabled && selectedTestDrivePoint) {
          sortedPoints = sortByActivePointId(
            filteredPoints,
            selectedTestDrivePoint._id
          );
        } else {
          sortedPoints = filteredPoints;
        }

        setFilteredTestDrivePoints(sortedPoints.filter(sp => sp.status));
      }
    );
  }, [
    locations,
    vehicles,
    typeFilter,
    address,
    selectedTestDrivePoint,
    isPointSortingEnabled,
    filteredTestDrivePointsLength,
    isHomeTestDriveAvailable,
  ]);

  useEffect(() => {
    gtm.fire(
      testDriveMapUpdateUrlEvent({
        pageTitle: router.asPath,
        affiliation,
        isSSRRendered,
      })
    );

    if (!address) {
      return;
    }

    gtm.fire(
      testDriveSearchResultsUpdateUrlEvent({
        pageTitle: router.asPath,
        affiliation,
        isSSRRendered,
      })
    );
  }, []);

  useEffect(() => {
    if (!map || filteredTestDrivePoints.length === 0) {
      return;
    }

    const targetMarkers = filteredTestDrivePoints.map(testDrivePoint => {
      const marker = {
        id: testDrivePoint._id,
        type: testDrivePoint.type,
        lat: Number(testDrivePoint.geoLatitude),
        lng: Number(testDrivePoint.geoLongitude),
        storeType: testDrivePoint.storeType,
        distanceInMeters: !selectedTestDrivePoint
          ? null
          : Math.round(
              calculateDistanceInMetersSync(
                map.maps,
                {
                  lat: parseFloat(testDrivePoint.geoLatitude),
                  lng: parseFloat(testDrivePoint.geoLongitude),
                },
                {
                  lat: parseFloat(selectedTestDrivePoint.geoLatitude),
                  lng: parseFloat(selectedTestDrivePoint.geoLongitude),
                }
              )
            ),
      };

      const size = marker.id === selectedTestDrivePoint ? 35 : 25;

      const mapMarker = map.createMarker({
        position: { lat: Number(marker.lat), lng: Number(marker.lng) },
        icon: {
          url: getTestDrivePointImage(marker.type, marker.storeType),
          scaledSize: new map.maps.Size(
            size,
            [
              TEST_DRIVE_POINT_STORE_TYPE.DARTY,
              TEST_DRIVE_POINT_STORE_TYPE.FNAC,
            ].includes(marker.storeType)
              ? size * 1.2
              : size
          ),
        },
      });

      mapMarker.addListener("click", () => {
        handleMarkerClick(marker.id);

        return handleSetAddress(null);
      });

      return mapMarker;
    });

    const clusterer = map.addClusterer(targetMarkers, {
      averageCenter: true,
      enableRetinaIcons: true,
      gridSize: 50,
      imagePath: getStaticAssetURL("/images/clusterer/m", {
        withVersion: false,
      }),
    });

    const mapIdleEvent = map.maps.event.addListenerOnce(
      map.instance,
      "idle",
      () => clusterer.repaint()
    );

    return () => {
      clusterer.removeMarkers(targetMarkers);
      map.maps.event.removeListener(mapIdleEvent);
    };
  }, [map, filteredTestDrivePoints, selectedTestDrivePoint]);

  useEffect(() => {
    if (!address || !map) {
      return;
    }

    const targetMarker = map.createMarker({
      position: { lat: address.geoLat, lng: address.geoLng },
      icon: {
        path: map.maps.SymbolPath.CIRCLE,
        scale: 8.5,
        fillColor: primaryColor,
        fillOpacity: 1,
        strokeWeight: 1,
        strokeColor: primaryColor,
      },
    });

    map.render(targetMarker);

    map.instance.panTo({ lat: address.geoLat, lng: address.geoLng });
    map.smoothZoom(12);

    return () => {
      map.remove(targetMarker);
    };
  }, [map, address]);

  return (
    <Wrapper isWholeWidth color={theme.colors.white}>
      <DesktopContainer>
        <MainContent data-id="sticky-boundary">
          <MapWrapper>
            <Sticky
              boundaryElement="[data-id=sticky-boundary]"
              hideOnBoundaryHit={false}
              positionRecheckInterval={50}
              disabled={!isDesktopMedia}
              className="sticky-holder"
              holderCmp={"section"}
            >
              <MapSection
                mapContainerRef={mapContainerRefDesktop}
                accentColor={theme.colors.testDriveAccent}
                handleSetAddress={handleSetAddress}
                typeFilter={typeFilter}
                setTypeFilter={setTypeFilter}
                testDriveTypes={types}
              />
            </Sticky>
          </MapWrapper>
          <DesktopTextContentWrapper>
            <TitleSection
              address={address}
              accentColor={theme.colors.testDriveAccent}
              shouldRenderAddress={locations.length > 0}
              onSetAddress={handleSetAddress}
              setIsTypeExplanationOpen={setIsTypeExplanationOpen}
              defaultColor={theme.colors.testDriveDefault}
              onResetAddress={handleResetAddress}
              typesContainerRef={typesContainerRef}
            />
            <TestDrivePointsSection
              ref={testDriveTypeWrapperRef}
              affiliation={affiliation}
              setIsTypeExplanationOpen={setIsTypeExplanationOpen}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              accentColor={theme.colors.testDriveAccent}
              testDrivePoints={filteredTestDrivePoints}
              selectedTestDrivePoint={selectedTestDrivePoint}
              isTypeExplanationOpen={isTypeExplanationOpen}
              typesContainerRef={typesContainerRef}
              defaultColor={theme.colors.testDriveDefault}
              testDriveTypes={types}
              address={address}
              isHomeTestDriveAvailable={isHomeTestDriveAvailable}
            />
          </DesktopTextContentWrapper>
        </MainContent>
      </DesktopContainer>

      <MobileContainer>
        <MainContent>
          <TitleSection
            address={address}
            accentColor={theme.colors.testDriveAccent}
            shouldRenderAddress={locations.length > 0}
            onSetAddress={handleSetAddress}
            setIsTypeExplanationOpen={setIsTypeExplanationOpen}
            defaultColor={theme.colors.testDriveDefault}
            onResetAddress={handleResetAddress}
            typesContainerRef={typesContainerRef}
          />
          <MapWrapper>
            <MapSection
              mapContainerRef={mapContainerRefMobile}
              accentColor={theme.colors.testDriveAccent}
              handleSetAddress={handleSetAddress}
              typeFilter={typeFilter}
              setTypeFilter={setTypeFilter}
              testDriveTypes={types}
            />
          </MapWrapper>
          <TestDrivePointsSection
            ref={testDriveTypeWrapperRef}
            affiliation={affiliation}
            setIsTypeExplanationOpen={setIsTypeExplanationOpen}
            typeFilter={typeFilter}
            setTypeFilter={setTypeFilter}
            accentColor={theme.colors.testDriveAccent}
            testDrivePoints={filteredTestDrivePoints}
            selectedTestDrivePoint={selectedTestDrivePoint}
            isTypeExplanationOpen={isTypeExplanationOpen}
            typesContainerRef={typesContainerRef}
            defaultColor={theme.colors.testDriveDefault}
            testDriveTypes={types}
            address={address}
            isHomeTestDriveAvailable={isHomeTestDriveAvailable}
          />
        </MainContent>
      </MobileContainer>
    </Wrapper>
  );
};

TestDrive.propTypes = {
  affiliation: string,
  locations: arrayOf(testDriveLocationType),
  types: arrayOf(testDriveType),
  vehicles: arrayOf(testDriveVehicleType),
  homeTestDriveLocations: arrayOf(homeTestDriveLocationType),
  preDefinedTestDriveType: string,
  isSSRRendered: bool,
};

export default TestDrive;
